import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui-bits";

/**
 * Admin-only control for the **shift cutoff hour** (0-23 UTC). Defines the
 * instant the operator-facing "Today" filters flip. For Oz Bakery the
 * default of 03:00 UTC matches the overnight bake-and-pack cycle.
 */
export default function ShiftSettingsPanel() {
  const [hour, setHour] = useState(3);
  const [defaultHour, setDefaultHour] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/shift-cutoff");
      setHour(data.hour);
      setDefaultHour(data.default);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shift cutoff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const h = Number(hour);
    if (!Number.isInteger(h) || h < 0 || h > 23) {
      toast.error("Hour must be a whole number 0-23");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings/shift-cutoff", { hour: h });
      toast.success("Shift cutoff updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-10 text-center text-stone-500">Loading…</div>;
  }

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <Panel title="Shift cutoff (Today / History scope)">
      <div className="space-y-5 max-w-md">
        <p className="text-sm text-stone-600 dark:text-stone-300">
          Operator screens (Production, Packing &amp; Labels, Waste, Dispatch,
          Finished Inventory&apos;s &quot;Today&quot; tab) default to{" "}
          <strong>today&apos;s shift</strong>. The shift flips at the cutoff
          hour below — pick the hour your bakery&apos;s &quot;day&quot;
          actually starts. Overnight bakeries usually set 02:00–06:00 so an
          overnight bake counts as that morning&apos;s shift.
        </p>

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider font-semibold">
              <Clock className="h-3.5 w-3.5 inline mr-1.5" />
              Cutoff hour (0-23 UTC)
            </Label>
            <Input
              type="number"
              min={0}
              max={23}
              step={1}
              value={hour}
              onChange={(e) => setHour(e.target.value)}
              className="h-11 mt-1 tnum text-center text-lg font-display"
              data-testid="shift-cutoff-hour"
            />
            <p className="text-xs text-stone-500 mt-1">
              Today&apos;s shift runs <strong>{pad(hour)}:00</strong> → next-day{" "}
              <strong>{pad(hour)}:00</strong> UTC. (Default {pad(defaultHour)}:00)
            </p>
          </div>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-11 px-6"
            data-testid="shift-cutoff-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-xs text-amber-900 dark:text-amber-200">
          Heads up: changing the cutoff retroactively re-buckets historical
          records — yesterday&apos;s &quot;today&quot; will move into
          yesterday&apos;s history. Stored data is never modified.
        </div>
      </div>
    </Panel>
  );
}
